import React from 'react';

const STATUS_LABELS = {
  assigned: 'Assigned',
  'in-progress': 'In progress',
  'review-pending': 'Review pending',
  completed: 'Completed',
  rejected: 'Rejected',
  incomplete: 'Incomplete',
};

const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

function labelFor(key, map) {
  return map[key] || (key ? String(key).replace(/-/g, ' ') : '—');
}

/**
 * Horizontal bar list for statusCounts / priorityCounts: [{ _id, count }]
 */
export function TaskCountBreakdownChart({ title, subtitle, items, labelMap = STATUS_LABELS, emptyText = 'No data.' }) {
  const list = Array.isArray(items) ? items : [];
  const max = Math.max(...list.map((x) => Number(x.count) || 0), 1);
  const total = list.reduce((s, x) => s + (Number(x.count) || 0), 0);

  if (!list.length) {
    return (
      <section className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden transition-shadow duration-300 hover:shadow-md">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <p className="px-4 py-6 text-sm text-gray-500 text-center">{emptyText}</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden transition-shadow duration-300 hover:shadow-md">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        <p className="text-xs text-gray-400 mt-1">Total: {total} tasks</p>
      </div>
      <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {list.map((row) => {
          const id = row._id;
          const count = Number(row.count) || 0;
          const w = (count / max) * 100;
          return (
            <li key={String(id)} className="px-4 py-2.5">
              <div className="flex justify-between text-xs font-medium text-gray-700 mb-1">
                <span>{labelFor(id, labelMap)}</span>
                <span className="text-gray-900">{count}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${w}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function StatusCountsChart({ statusCounts }) {
  return (
    <TaskCountBreakdownChart
      title="Tasks by status"
      subtitle="How work is distributed across workflow states."
      items={statusCounts}
      labelMap={STATUS_LABELS}
      emptyText="No status breakdown returned."
    />
  );
}

export function PriorityCountsChart({ priorityCounts }) {
  return (
    <TaskCountBreakdownChart
      title="Open work by priority"
      subtitle="Non-completed tasks grouped by priority (when API provides this)."
      items={priorityCounts}
      labelMap={PRIORITY_LABELS}
      emptyText="No priority breakdown returned."
    />
  );
}
